/**
 * Таблица журнала взаимодействий.
 * Дата: 17.07.2026
 */
import { format } from 'date-fns';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { formatPhone } from '@/utils/formatters';
import {
  CHANNEL_ICONS,
  CHANNEL_LABELS,
  EVENT_TYPE_COLORS,
  EVENT_TYPE_LABELS,
  type InteractionEvent,
} from '@/types/interaction';
import {
  getInteractionAppointmentLabel,
  getInteractionFlowLabel,
  getInteractionPatientDisplayName,
  getInteractionScenarioLabel,
  getInteractionStatusMeta,
} from '@/features/interactions/utils/interactionDisplay';

const stickyHead =
  'sticky top-0 z-20 bg-shell-elevated/95 backdrop-blur-sm shadow-[inset_0_-1px_0_var(--shell-border)]';

export function InteractionsTable({
  events,
  selectedId,
  onSelect,
}: {
  events: InteractionEvent[];
  selectedId?: string | null;
  onSelect?: (id: string) => void;
}) {
  return (
    <Card className="border-shell-border bg-shell-surface shadow-none">
      <CardHeader className="space-y-1 pb-3">
        <CardTitle className="text-base font-extrabold tracking-tight text-shell-text">
          Журнал событий
        </CardTitle>
        <p className="text-sm text-shell-muted">{events.length} на странице</p>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="max-h-[min(70vh,52rem)] overflow-auto rounded-2xl border border-shell-border bg-shell-surface">
          <Table wrapped={false} className="min-w-full">
            <TableHead>
              <TableRow className="hover:bg-transparent">
                <TableHeader className={`${stickyHead} px-3 py-2.5`}>Время</TableHeader>
                <TableHeader className={`${stickyHead} px-3 py-2.5`}>Пациент</TableHeader>
                <TableHeader className={`${stickyHead} px-3 py-2.5`}>Телефон</TableHeader>
                <TableHeader className={`${stickyHead} px-3 py-2.5`}>Тип</TableHeader>
                <TableHeader className={`${stickyHead} px-3 py-2.5`}>Канал</TableHeader>
                <TableHeader className={`${stickyHead} px-3 py-2.5`}>Статус</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event) => {
                const status = getInteractionStatusMeta(event.status);
                const appointmentLabel = getInteractionAppointmentLabel(event);
                const scenarioLabel = getInteractionScenarioLabel(event);
                const flowLabel = getInteractionFlowLabel(event);
                const ctaLabel =
                  typeof event.metadata?.cta_label === 'string'
                    ? event.metadata.cta_label.trim()
                    : '';

                return (
                  <TableRow
                    key={event.id}
                    className={selectedId === event.id ? 'bg-shell-primary-soft' : undefined}
                    onClick={() => onSelect?.(event.id)}
                  >
                    <TableCell className="whitespace-nowrap px-3 py-2.5 text-shell-muted">
                      {format(new Date(event.created_at), 'dd.MM HH:mm')}
                    </TableCell>
                    <TableCell className="px-3 py-2.5 whitespace-normal">
                      <div className="font-semibold text-shell-text">
                        {getInteractionPatientDisplayName(event) || '—'}
                      </div>
                      {appointmentLabel ? (
                        <div className="mt-1 text-xs text-shell-muted">
                          Запись: {appointmentLabel}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell className="px-3 py-2.5 text-shell-muted">
                      {formatPhone(event.phone)}
                    </TableCell>
                    <TableCell className="px-3 py-2.5 whitespace-normal">
                      <Badge
                        className={`text-[11px] ${EVENT_TYPE_COLORS[event.event_type] || 'bg-shell-hover text-shell-text'}`}
                      >
                        {EVENT_TYPE_LABELS[event.event_type] || event.event_type}
                      </Badge>
                      {scenarioLabel ? (
                        <div className="mt-1 text-xs text-shell-muted">{scenarioLabel}</div>
                      ) : null}
                      {ctaLabel ? (
                        <div className="mt-0.5 text-xs text-shell-muted">CTA: {ctaLabel}</div>
                      ) : null}
                    </TableCell>
                    <TableCell className="px-3 py-2.5 whitespace-normal">
                      <div className="flex flex-wrap items-center gap-1.5 text-sm text-shell-muted">
                        <span>
                          {CHANNEL_ICONS[event.source_channel] || ''}{' '}
                          {CHANNEL_LABELS[event.source_channel] || event.source_channel}
                        </span>
                        {flowLabel ? (
                          <span className="rounded-full border border-shell-success/20 bg-shell-success-soft px-2 py-0.5 text-[10px] font-semibold text-shell-success">
                            {flowLabel}
                          </span>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="px-3 py-2.5">
                      <Badge className={`border text-[10px] ${status.className}`}>
                        {status.label}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
